import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types'
import { apiClient } from '@/services/api'

interface AuthState {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

interface AuthActions {
  login: (email: string, password: string, rememberMe?: boolean, captchaToken?: string) => Promise<void>
  register: (userData: {
    email: string
    password: string
    password_confirmation: string
    first_name: string
    last_name: string
    referral_code?: string
  }, captchaToken?: string) => Promise<string | void>
  logout: () => Promise<void>
  setUser: (user: User) => void
  setToken: (token: string) => void
  clearAuth: () => void
  checkAuth: () => Promise<void>
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // Actions
      login: async (email: string, password: string, rememberMe?: boolean, captchaToken?: string) => {
        try {
          set({ isLoading: true, error: null })

          // Let the API client handle timeouts
          const response = await apiClient.login(email, password, captchaToken, rememberMe)
          
          const { user, token } = response.data
          
          // Store token and user data in localStorage
          localStorage.setItem('auth_token', token)
          localStorage.setItem('cached_user', JSON.stringify(user))

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          let errorMessage = 'Login failed'

          if (error.message?.includes('timed out') || error.code === 'ECONNABORTED') {
            errorMessage = 'Login request timed out. Please try again or check your connection.'
          } else if (error.response?.data?.error?.message) {
            errorMessage = error.response.data.error.message
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message
          }
          
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: errorMessage,
          })
          throw error
        }
      },

      register: async (userData: {
        email: string
        password: string
        password_confirmation: string
        first_name: string
        last_name: string
        referral_code?: string
      }, captchaToken?: string) => {
        try {
          set({ isLoading: true, error: null })

          const response = await apiClient.signUp(userData, captchaToken)

          // Don't auto-login — user must verify email first
          set({
            isLoading: false,
            error: null,
          })

          return response.data?.message || 'Please check your email to verify your account.'
        } catch (error: any) {
          let errorMessage = 'Registration failed'

          if (error.message?.includes('timed out') || error.code === 'ECONNABORTED') {
            errorMessage = 'Registration request timed out. Please try again or check your connection.'
          } else if (error.response?.data?.error?.message) {
            errorMessage = error.response.data.error.message
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message
          } else if (error.response?.data?.errors) {
            const errors = error.response.data.errors
            errorMessage = Object.values(errors).flat().join(', ')
          }

          set({
            isLoading: false,
            error: errorMessage,
          })
          throw error
        }
      },

      logout: async () => {
        try {
          await apiClient.logout()
        } catch (error) {
          // silently ignore
        } finally {
          // Clear auth state regardless of API response
          localStorage.removeItem('auth_token')
          set({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
          })
        }
      },

      setUser: (user: User) => {
        set({ user, isAuthenticated: true })
      },

      setToken: (token: string) => {
        localStorage.setItem('auth_token', token)
        set({ token, isAuthenticated: true })
      },

      clearAuth: () => {
        localStorage.removeItem('auth_token')
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          error: null,
        })
      },

      checkAuth: async () => {
        const token = localStorage.getItem('auth_token')

        if (!token) {
          set({ isAuthenticated: false, isLoading: false })
          return
        }

        const alreadyAuthenticated = get().isAuthenticated

        // Only use cache on initial load — not on refresh calls where the store
        // already has fresher data (e.g. after credit-changing operations)
        if (!alreadyAuthenticated) {
          const cachedUser = localStorage.getItem('cached_user') || localStorage.getItem('user')
          if (cachedUser) {
            try {
              const user = JSON.parse(cachedUser)
              set({
                user,
                token,
                isAuthenticated: true,
                isLoading: false,
                error: null,
              })
            } catch (e) {
              localStorage.removeItem('cached_user')
            }
          }
        }

        try {
          if (!alreadyAuthenticated && !get().user) set({ isLoading: true })

          const response = await apiClient.getCurrentUser()

          const user = response.data.user

          // Update cache and store with fresh data
          localStorage.setItem('cached_user', JSON.stringify(user))

          set({
            user,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error: any) {
          // Handle timeout errors gracefully without noisy logging
          if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
            set({
              isLoading: false,
              error: null,
            })
            return
          }

          // Only clear auth on 401 (invalid token), not on 5xx server errors
          if (error.response?.status === 401) {
            localStorage.removeItem('auth_token')
            localStorage.removeItem('cached_user')
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
              error: null,
            })
          } else {
            // Server error - keep cached state, just stop loading
            set({ isLoading: false })
          }
        }
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading })
      },

      setError: (error: string | null) => {
        set({ error })
      },

      clearError: () => {
        set({ error: null })
      },
    }),
    {
      name: 'armada-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

export const useIsAdmin = () => useAuthStore(state => state.user?.role === 'admin')
export const useIsCaptain = () => useAuthStore(state => ['captain', 'admin'].includes(state.user?.role ?? ''))
export const useUserRole = () => useAuthStore(state => state.user?.role ?? 'pirate')